<template>
  <div class="flex flex-col">
    <selected-files-component v-if="selectedFiles.length" />
    <div
      class="command-input-wrapper relative"
      :class="[{ 'is-focused': isFocused }, isChat ? 'mr-0 ml-0' : 'mr-6 ml-4']"
    >
      <lf-text-editor
        ref="commandInputRef"
        class="command-input"
        content-type="html"
        :value="agentSessionInfo.htmlSearchContent"
        name="command-input"
        :modules="modules"
        :toolbar="[]"
        data-cy="cmd-k-command-input"
        :placeholder="placeholder"
        @update:content="handleContentUpdate"
        @keydown.enter="handleCommandSubmit"
        @click="handleClick"
        @blur="isFocused = false"
      />
      <voice-prompt
        ref="voicePromptRef"
        class="absolute top-1/2 right-2 -translate-y-1/2"
        data-cy="voice-prompt-button"
        @text:updated="handleVoiceTextUpdate"
        @recording:finished="handleVoiceTextFinished"
      />
      <file-uploader
        :disabled="fileUploadingDisabled"
        class="absolute top-1/2 -translate-y-1/2"
        :class="isChat ? 'right-14' : 'right-5'"
      />
      <slot name="actions" />
    </div>
  </div>
</template>
<script setup lang="ts">
import { onKeyStroke } from "@vueuse/core";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

import { Quill } from "@vueup/vue-quill";
import { storeToRefs } from "pinia";
import Mention from "quill-mention";

import { AiDialogType } from "@/enums/ai";
import { AnalyticsEvent } from "@/enums/analytics";
import { stripHTMLTagsFromString } from "@/helpers/common";
import {
  AI_AGENTS_REGEX,
  NON_BREAKING_SPACE,
} from "@/helpers/constants/aiAgents";
import { handleDenotationTags, removeDenotation } from "@/helpers/textEditor";
import { AgentMentionBlot, DealMentionBlot } from "@/lib/Quill";
import { useAiAgentsStore } from "@/stores/ai/agents";
import { useFiles } from "@/stores/ai/files";
import { useAnalyticsStore } from "@/stores/analytics";

import VoicePrompt from "@/components/VoicePrompt.vue";
import FileUploader from "@/components/cmdK/chat/FileUploader.vue";
import SelectedFilesComponent from "@/components/cmdK/chat/files/SelectedFiles.vue";
import LfTextEditor from "@/components/ui/LfTextEditor.vue";

import type { Delta } from "@vueup/vue-quill";

const {
  placeholder,
  type,
  isChat = false,
  fileUploadingDisabled = false,
} = defineProps<{
  placeholder: string;
  type: AiDialogType;
  fileUploadingDisabled?: boolean;
  isChat?: boolean;
}>();

const emit = defineEmits<{
  "command:update": [string];
  "agent:remove": [];
  "command:submit": [];
}>();

Quill.register(AgentMentionBlot);
Quill.register(DealMentionBlot);

const aiFilesStore = useFiles();
const aiAgentsStore = useAiAgentsStore();
const { trackEvent } = useAnalyticsStore();
const { agentSessionInfo } = storeToRefs(aiAgentsStore);
const { selectedFiles } = storeToRefs(aiFilesStore);

const searchValue = ref("");
const isFocused = ref(true);
const voiceTextPosition = ref<number | null>(null);
const voiceTextLength = ref(0);
const commandInputRef = useTemplateRef("commandInputRef");
const voicePromptRef = useTemplateRef("voicePromptRef");

const modules = computed(() => [
  {
    name: "agentMention",
    module: Mention,
    blotName: "agentMention",
    options: {
      allowedChars: /^[A-Za-z\s]*$/,
      mentionDenotationChars: ["@"],
      dataAttributes: ["tag", "name"],
      blotName: "agentMention",
      source: function (
        searchTerm: string,
        renderList: () => void,
        dynamicChar: string
      ) {
        if (dynamicChar !== "@") {
          return;
        }

        searchValue.value = searchTerm;
      },
    },
  },
  {
    name: "dealMention",
    module: Mention,
    blotName: "dealMention",
    options: {
      allowedChars: /^[A-Za-z\s]*$/,
      mentionDenotationChars: ["/"],
      dataAttributes: ["tag", "name"],
      blotName: "dealMention",
      source: function (
        searchTerm: string,
        renderList: () => void,
        dynamicChar: string
      ) {
        if (dynamicChar !== "/") {
          return;
        }

        searchValue.value = searchTerm;
      },
    },
  },
  {
    name: "keyboard",
    module: Quill.import("modules/keyboard"),
    options: {
      bindings: {
        handleEnter: {
          key: 13, // Enter key
          handler: () => {
            /**
             * return true to let Quill continue default behavior
             * return false to prevent default
             * returning false so that enter would actually submit the data
             * and not add a new line (for that we use shift + enter)
             */
            return false;
          },
        },
      },
    },
  },
]);

/**
 * Find the position after the last inserted mention (agent or deal)
 */
const findLastInsertedMentionPosition = (): number => {
  if (!commandInputRef.value?.editorInstance) {
    return 0;
  }

  const editorInstance = commandInputRef.value.editorInstance;
  const contents: Delta = editorInstance.getContents();

  let lastMentionEndPosition = 0;
  let currentPosition = 0;

  contents.ops?.forEach((op) => {
    if (!op.insert) {
      return;
    }

    if (typeof op.insert === "object") {
      // This is an embed (mention)
      const insertObj = op.insert as Record<string, unknown>;

      if (insertObj.agentMention || insertObj.dealMention) {
        lastMentionEndPosition = currentPosition + 1; // Position after the mention
      }

      currentPosition += 1; // Embeds count as 1 character
    } else if (typeof op.insert === "string") {
      currentPosition += op.insert.length;
    }
  });
  return lastMentionEndPosition;
};

const handleClick = () => {
  isFocused.value = true;
  voicePromptRef.value?.stopRecording();
};

const handleDealSearchInsertion = (
  editorInstance: Quill,
  startPosition: number,
  endPosition: number
) => {
  const contents = editorInstance.getContents();
  const newOps: Record<string, unknown>[] = [];
  let currentPos = 0;

  contents.ops?.forEach((op: Record<string, unknown>) => {
    if (!op.insert) {
      return;
    }

    if (typeof op.insert === "object") {
      // This is an embed (mention) - always keep it exactly as is
      newOps.push({ ...op });
      currentPos += 1;
      return;
    }

    if (typeof op.insert === "string") {
      // This is text - check if we need to split it
      const textStart = currentPos;
      const textEnd = currentPos + op.insert.length;

      if (textEnd <= startPosition || textStart >= endPosition) {
        // Text is completely outside deletion range - keep all of it
        newOps.push(op);
        currentPos += op.insert.length;
        return;
      }

      // Text overlaps with deletion range - split it
      const beforeDeletion = op.insert.substring(
        0,
        Math.max(0, startPosition - textStart)
      );

      const afterDeletion = op.insert.substring(
        Math.max(0, endPosition - textStart)
      );

      if (beforeDeletion) {
        newOps.push({
          insert: beforeDeletion,
          attributes: op.attributes,
        });
      }

      if (afterDeletion) {
        newOps.push({
          insert: afterDeletion,
          attributes: op.attributes,
        });
      }

      currentPos += op.insert.length;
    }
  });

  // Set the new content using the rebuilt ops
  editorInstance.setContents({ ops: newOps });
};

const insertItem = (type: "agent" | "deal", name: string, value: string) => {
  if (!commandInputRef.value?.editorInstance) {
    return;
  }
  const editorInstance = commandInputRef.value.editorInstance;
  editorInstance.focus();
  const cursorPosition = editorInstance.getSelection()?.index;

  if (cursorPosition !== 0 && !cursorPosition) {
    return;
  }

  /**
   * If we are not in the agent mode and we are inserting a deal
   * we need to delete all the text up to the last inserted mention
   * because this text represents the deal search phrase
   */

  const isDealSearch =
    type === "deal" && !agentSessionInfo.value.agent && !isChat;

  let startPosition: number;
  let endPosition: number;

  if (isDealSearch) {
    // For deal search, we want to delete all text from after the last mention to cursor
    const lastInsertedMentionPosition = findLastInsertedMentionPosition();

    // If there are no mentions, delete from beginning
    if (lastInsertedMentionPosition === 0) {
      startPosition = 0;
      endPosition = cursorPosition;
    } else {
      // Delete everything after the last mention position
      startPosition = lastInsertedMentionPosition;
      endPosition = cursorPosition;
    }
  } else {
    // For agent search, delete the @ symbol and search term
    startPosition = cursorPosition - searchValue.value.length - 1;
    endPosition = cursorPosition;
  }

  // Use a safe deletion method that preserves embeds
  if (isDealSearch) {
    // For deal search, we need to preserve embeds while deleting text
    handleDealSearchInsertion(editorInstance, startPosition, endPosition);
  } else {
    /**
     * For agent mentions, the simple deleteText should work fine
     * since no embeds yet
     */
    editorInstance.deleteText(startPosition, endPosition);
  }

  editorInstance.deleteText(startPosition, endPosition);

  const embedPayload = {
    name: type === "agent" ? "agentMention" : "dealMention",
    value:
      type === "agent" ? { agent: name, id: value } : { name, link: value },
  };

  let embedPosition = startPosition;

  if (isDealSearch && startPosition > 0) {
    /**
     * Add non-breaking space to separate previous mention and a new one
     * Not needed when there is no mention before
     */
    editorInstance.insertText(startPosition, NON_BREAKING_SPACE);
    embedPosition = startPosition + 1; // Account for the space we just inserted
  }

  editorInstance.insertEmbed(
    embedPosition,
    embedPayload.name,
    embedPayload.value
  );

  handleDenotationTags();
  searchValue.value = "";

  const lastInsertedMentionPositionAfterInsert =
    findLastInsertedMentionPosition();

  if (lastInsertedMentionPositionAfterInsert === null) {
    return;
  }

  editorInstance.setSelection(lastInsertedMentionPositionAfterInsert);
  /**
   * Add non-breaking space to allow users to type after inserting agent/deal mention
   */
  editorInstance.insertText(
    lastInsertedMentionPositionAfterInsert,
    NON_BREAKING_SPACE
  );
  editorInstance.setSelection(lastInsertedMentionPositionAfterInsert + 1);

  setTimeout(() => {
    const editorEl = editorInstance.root;
    const paragraphs: NodeListOf<Element> = editorEl.querySelectorAll("p");
    const lastParagraph = paragraphs[paragraphs.length - 1];
    if (
      lastParagraph?.children?.length === 1 &&
      lastParagraph?.children[0]?.tagName === "BR"
    ) {
      lastParagraph.remove();
    }
  }, 0);
};

const handleContentUpdate = (value: string) => {
  isFocused.value = true;

  const contents: Delta = commandInputRef.value?.editorInstance.getContents();

  const hasContent = contents?.ops.some(
    (content) => content.insert !== "\n" && content.insert !== ""
  );

  if (!hasContent) {
    agentSessionInfo.value.htmlSearchContent = "";
    emit("command:update", "");
    return;
  }

  const { clearContentValue, clearContentWithNewLines } = clearContent(value);

  const emitValue = [AiDialogType.AgentChat, AiDialogType.Sessions].includes(
    type
  )
    ? clearContentWithNewLines
    : clearContentValue;

  emit("command:update", emitValue);
};

const handleCommandSubmit = (event: KeyboardEvent) => {
  if (event.shiftKey) {
    return;
  }

  if (agentSessionInfo.value.agent || isChat) {
    emit("command:submit");
  }
};

const clearContent = (htmlString: string | undefined) => {
  if (!htmlString) {
    return { clearContentValue: "", clearContentWithNewLines: "" };
  }

  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  agentSessionInfo.value.htmlSearchContent = doc.body.innerHTML;

  // Get all child elements within the container
  const allElements = Array.from(doc.body.querySelectorAll("*"));

  allElements.forEach((el) => {
    // Handle agent mentions - remove entire outer span
    if (el.matches("span.mention[data-agent]")) {
      el.remove();
      return;
    }
    // Handle link mentions - replace with URL from inner anchor
    if (el.matches("span.mention[data-link]")) {
      // Look for the inner anchor tag first
      const innerLink = el.querySelector("a[href]");
      const href =
        innerLink?.getAttribute("href") || el.getAttribute("data-link");

      if (href) {
        const textNode = doc.createTextNode(href);
        el.parentNode?.replaceChild(textNode, el);
      } else {
        el.remove();
      }
      return;
    }

    // Handle any remaining elements with denotation chars
    if (el.matches(".ql-mention-denotation-char")) {
      removeDenotation(el);
      return;
    }
  });

  /**
   * For prompts and chat messages we need to keep new lines to display them correctly.
   * Only agent prompt and chat messages require this.
   */
  const clearContentValue = stripHTMLTagsFromString(doc.body.innerHTML);
  const clearContentWithNewLines = stripHTMLTagsFromString(
    doc.body.innerHTML,
    true,
    true
  );

  return { clearContentValue, clearContentWithNewLines };
};

const setCursorToLastPosition = () => {
  if (!commandInputRef.value?.editorInstance) {
    return;
  }
  const editorInstance = commandInputRef.value.editorInstance;
  const length = editorInstance.getLength();
  editorInstance.setSelection(length - 1);

  scrollToBottom();
};

const scrollToBottom = () => {
  if (!commandInputRef.value?.editorInstance) {
    return;
  }
  const editorInstance = commandInputRef.value.editorInstance;

  const editor = editorInstance.container.querySelector(".ql-editor");

  if (!editor) {
    return;
  }

  editor.scrollTop = editor.scrollHeight;
};

const handleCapitalization = () => {
  if (!commandInputRef.value?.editorInstance) {
    return;
  }

  const editorInstance = commandInputRef.value.editorInstance;
  const links: NodeListOf<Element> =
    editorInstance.container.querySelectorAll(".link-tag");

  links.forEach((link) => {
    link.classList.add("capitalize");
  });
};

const handleVoiceTextUpdate = (text: string) => {
  if (!commandInputRef.value?.editorInstance) {
    return;
  }
  const editorInstance = commandInputRef.value.editorInstance;
  editorInstance.focus();
  const cursorPosition = editorInstance.getSelection()?.index;

  if (cursorPosition !== 0 && !cursorPosition) {
    return;
  }

  searchValue.value = "";

  // If this is the first voice text insertion, store the position
  if (voiceTextPosition.value === null) {
    voiceTextPosition.value = cursorPosition;
  }

  // Remove previously inserted voice text if any
  if (voiceTextLength.value > 0 && voiceTextPosition.value !== null) {
    editorInstance.deleteText(voiceTextPosition.value, voiceTextLength.value);
  }

  // Insert the new voice text
  if (voiceTextPosition.value !== null) {
    editorInstance.insertText(voiceTextPosition.value, text);
    voiceTextLength.value = text.length;

    // Keep cursor at the end of the inserted text
    editorInstance.setSelection(voiceTextPosition.value + text.length);
    scrollToBottom();
  }
};

const deleteAllContents = () => {
  if (!commandInputRef.value?.editorInstance) {
    return;
  }
  const editorInstance = commandInputRef.value.editorInstance;
  editorInstance.deleteText(0, editorInstance.getLength());
};

const handleVoiceTextFinished = () => {
  if (voiceTextLength.value > 0) {
    trackEvent(AnalyticsEvent.CmdKVoiceInputUsed, {
      text_length: voiceTextLength.value,
    });
  }
  voiceTextPosition.value = null;
  voiceTextLength.value = 0;
};

watch(
  () => agentSessionInfo.value.htmlSearchContent,
  (newContent, oldContent) => {
    const newContentHasAgentTile = new RegExp(
      AI_AGENTS_REGEX.AGENT_TILE.source,
      "gs"
    ).test(newContent);
    const oldContentHasAgentTile = new RegExp(
      AI_AGENTS_REGEX.AGENT_TILE.source,
      "gs"
    ).test(oldContent);

    if (!newContentHasAgentTile && oldContentHasAgentTile) {
      emit("agent:remove");
    }

    if (
      stripHTMLTagsFromString(newContent).at(-1) !==
      stripHTMLTagsFromString(oldContent).at(-1)
    ) {
      scrollToBottom();
    }

    handleCapitalization();
  }
);

onKeyStroke(() => {
  voicePromptRef.value?.stopRecording();
});

onMounted(() => {
  commandInputRef.value?.editorInstance.focus();
  nextTick(setCursorToLastPosition);
});

watch(
  () => agentSessionInfo.value.htmlSearchContent,
  (updatedValue) => {
    if (!stripHTMLTagsFromString(updatedValue).length) {
      voicePromptRef.value?.stopRecording();
    }
  }
);

defineExpose({
  insertItem,
  deleteAllContents,
});
</script>

<style>
@reference "@/assets/tailwind.css";

.command-input {
  @apply min-h-10 w-full;
}

.command-input .ql-editor {
  @apply w-full border-none p-0 py-2.5 text-headline;
}

.command-input .agent-tag {
  @apply mx-1 rounded bg-disabled px-2 py-1 text-xs font-semibold text-label uppercase;
}

.command-input .link-tag {
  @apply text-primary;
}

.command-input.ql-container {
  @apply min-h-10! rounded-[0.375rem] border-none! pr-14 pl-4;
}

.chat .command-input-wrapper .ql-container {
  @apply pr-25;
}

.command-input .ql-editor {
  @apply min-h-10! px-0!;
}

.command-input .ql-editor p {
  @apply p-px!;
}

.command-input .ql-toolbar {
  @apply hidden;
}

.command-input-wrapper {
  @apply rounded border border-disabled-medium;
}

.command-input-wrapper.is-focused {
  @apply border-success;
}

/* Auto-growing height up to 3 lines, then scrollbar */
.command-input .ql-container {
  @apply max-h-22 min-h-10;
}

.command-input .ql-editor {
  @apply max-h-22 min-h-10 overflow-y-auto;
}
</style>
